import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Demo flow step definitions
// ---------------------------------------------------------------------------

export interface DemoStep {
  step: number;
  title: string;
  path: string;
  nextLabel: string;
  nextPath: string;
}

export const DEMO_STEPS: DemoStep[] = [
  { step: 1, title: 'Protocol Upload & Parse',      path: '/protocol-setup', nextLabel: 'Next: Enroll Patients',          nextPath: '/safety' },
  { step: 2, title: 'Patient Enrollment',            path: '/safety',         nextLabel: 'Next: Log Visit & Samples',     nextPath: '/samples' },
  { step: 3, title: 'Visit Execution',               path: '/samples',        nextLabel: 'Next: Validate & Verify',       nextPath: '/validation' },
  { step: 4, title: 'Real-Time SDV & Validation',    path: '/validation',     nextLabel: 'Next: View Protocol Deviations', nextPath: '/deviations' },
  { step: 5, title: 'Evidence Capture',               path: '/deviations',     nextLabel: 'Next: View Evidence Timeline',  nextPath: '/evidence' },
  { step: 6, title: 'Decision Support',              path: '/evidence',       nextLabel: 'Next: Decision & Go/No-Go',     nextPath: '/decisions' },
  { step: 7, title: 'Audit & Inspection Ready',      path: '/decisions',      nextLabel: 'Next: IP Accountability & Audit', nextPath: '/ip' },
];

// IP Accountability is the final page
export const DEMO_FINAL_PATH = '/ip';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface DemoContextType {
  isDemoActive: boolean;
  startDemo: () => void;
  exitDemo: () => void;
  currentStepForPath: (path: string) => DemoStep | null;
  isFinalStep: (path: string) => boolean;
}

const DemoContext = createContext<DemoContextType>({
  isDemoActive: false,
  startDemo: () => {},
  exitDemo: () => {},
  currentStepForPath: () => null,
  isFinalStep: () => false,
});

export function DemoProvider({ children }: { children: ReactNode }) {
  const [isDemoActive, setIsDemoActive] = useState<boolean>(() => {
    try { return localStorage.getItem('d3ri_demo_active') === 'true'; } catch { return false; }
  });

  const startDemo = useCallback(() => {
    setIsDemoActive(true);
    try { localStorage.setItem('d3ri_demo_active', 'true'); } catch { /* ignore */ }
  }, []);

  const exitDemo = useCallback(() => {
    setIsDemoActive(false);
    try { localStorage.removeItem('d3ri_demo_active'); } catch { /* ignore */ }
  }, []);

  const currentStepForPath = useCallback((path: string): DemoStep | null => {
    return DEMO_STEPS.find((s) => s.path === path) || null;
  }, []);

  const isFinalStep = useCallback((path: string): boolean => {
    return path === DEMO_FINAL_PATH;
  }, []);

  return (
    <DemoContext.Provider value={{ isDemoActive, startDemo, exitDemo, currentStepForPath, isFinalStep }}>
      {children}
    </DemoContext.Provider>
  );
}

export function useDemo() {
  return useContext(DemoContext);
}

export default DemoContext;
